import { useEffect } from 'react';
import { create } from 'zustand';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { listPage } from '@/models/listPage';
import { COMPANY_ROLE, TIME_SHEET_TYPE } from '@/models/references/references';
import { useCurrentCompany } from '@/stores/useCompanyStore';
import { resolveReference } from '@/stores/useReferenceOptions';

/** Absences listées dans l'encart de Mes demandes (le compteur, lui, les compte toutes) */
export const ABSENCES_TO_SIGN_SHOWN = 10;

interface AbsencesToSignStore {
  /** Rattachement suivi (null : aucun, ou pas employé de la société) */
  memberId: number | null;
  /** Ses absences à signer, la plus récente période d'abord (au plus ABSENCES_TO_SIGN_SHOWN) */
  sheets: TimeSheetModel[];
  /** Leur nombre total */
  count: number;
  /** Suit les absences à signer de ce rattachement */
  follow: (memberId: number | null) => Promise<void>;
  /** Les recharge (après une signature) */
  refresh: () => Promise<void>;
}

let followed: number | null | undefined;

/**
 * Absences que l'employé connecté doit encore signer, dans la société sélectionnée (GET /time_sheets?member=…
 * &timeSheetType=<absence>&employeeSigned=false : une requête, liste et total) : compteur de « Mes Demandes » dans la
 * sidebar, encart en tête de Mes demandes (pas de bandeau sur la Feuille d'heure pour l'instant, chef de projet). Rechargées après sa signature.
 */
export const useAbsencesToSign = create<AbsencesToSignStore>((set, get) => ({
  memberId: null,
  sheets: [],
  count: 0,

  follow: async (memberId) => {
    if (followed === memberId) return;
    followed = memberId;
    set({ memberId, sheets: [], count: 0 });
    await get().refresh();
  },

  refresh: async () => {
    const { memberId } = get();
    if (memberId === null) return;
    try {
      const absence = await resolveReference('timeSheetTypes', TIME_SHEET_TYPE.ABSENCE);
      const { models, totalItems } = await listPage(
        TimeSheetModel,
        {
          filters: { member: memberId, timeSheetType: absence.id, employeeSigned: 'false' },
          order: { 'period.month': 'desc' },
          page: 1,
          itemsPerPage: ABSENCES_TO_SIGN_SHOWN,
        },
        [['period']],
      );
      // Société changée pendant le chargement : la réponse ne vaut plus
      if (get().memberId === memberId) set({ sheets: models, count: totalItems });
    } catch {
      // Indisponible : pas de rappel, les demandes restent consultables
    }
  },
}));

/** Rattachement de l'employé qui signe ses absences : employé ou assistant de la société (pas le Dirigeant) */
export function signerMemberId(company: { companyRole: string | null; memberId: number | null } | null): number | null {
  return company && company.companyRole !== COMPANY_ROLE.DIRECTOR ? company.memberId : null;
}

/** Suit les absences à signer de l'employé de la société sélectionnée (monté une fois, dans la sidebar) */
export function useFollowAbsencesToSign(): number {
  const memberId = signerMemberId(useCurrentCompany());
  const { count, follow } = useAbsencesToSign();

  useEffect(() => {
    void follow(memberId);
  }, [memberId, follow]);

  return memberId === null ? 0 : count;
}
